import { useCallback, useMemo, useState } from "react"

import { TextMatch } from "shared/text"
import { safeParseDate } from "shared/util"

import { ArticleResult, ArticlesAuthor, useListArticleBySisetu } from "interface/article"

import { useSearch } from "features/search/context"
import { getJournalDate } from "features/article/util"
import { ArticleYearStats, MeshStats, PublicationTypeStats } from "features/article/types"

type useSisetusArticlePageProps = {
  sisetuCode: string
}
export const useSisetusArticlePage = ({
	sisetuCode
}: useSisetusArticlePageProps) => {
	const search = useSearch()
	
	const [searchWord, setSearchWord] = useState<string>('')
	const [selectedYear, setSelectedYear] = useState<number | null>(null)
	const [selectedAuthorId, setSelectedAuthorId] = useState<number | null>(null)
	const [selectedMesh, setSelectedMesh] = useState<string | null>(null)
	const [selectedPublicationType, setSelectedPublicationType] = useState<string | null>(null)

	const listState = useListArticleBySisetu(
		search?.searchId,
		sisetuCode,
		{ updateTime: search?.updateTime }
	)

	const filterdArticleList = useMemo<ArticleResult[]>(
		() => {
			if (!listState.data) return []
			if (searchWord !== '') {
				const match = new TextMatch(searchWord)
				return listState.data.filter(record => match.match(
					record.title,
					record.titleJpn,
					record.journal.title,
					record.abstract,
					record.abstractJpn
				))
			} else {
				return listState.data
			}
		},
		[listState.data, searchWord]
	)

	const isFiltered = useCallback(
		(article: ArticleResult, condition?: {
			selectedYear?: number | null
			selectedAuthorId?: number | null
			selectedMesh?: string | null
			selectedPublicationType?: string | null
		}) => {
			if (condition?.selectedYear) {
				const year = safeParseDate(getJournalDate(article))?.getFullYear()
				if (year !== condition.selectedYear) return true
			}
			if (condition?.selectedAuthorId) {
				if (!article.authors || !article.authors.some(
					author => author.authorId === condition.selectedAuthorId)
				) return true
			}
			if (condition?.selectedMesh) {
				if (!article.meshs || !article.meshs.some(
					mesh => mesh.description === condition.selectedMesh)
				) return true
			}
			if (condition?.selectedPublicationType) {
				if (!article.publicationType || !article.publicationType.some(
					pub => pub.code === condition.selectedPublicationType)
				) return true
			}
			return false
		},
		[]
	)

	const yearsStats = useMemo<ArticleYearStats>(
		() => {
			const list: { year: number, count: number }[] = []
			let startYear: number | null = null
			let lastYear: number | null = null
			let maxCount = 0
			filterdArticleList
				.filter(article => isFiltered(
					article, { selectedAuthorId, selectedMesh, selectedPublicationType }) === false
				)
				.forEach(article => {
					const year = safeParseDate(getJournalDate(article))?.getFullYear()
					if (!year) return

					if (!startYear || startYear > year) startYear = year
					if (!lastYear || lastYear < year) lastYear = year

					const exist = list.find(exist => exist.year === year)
					if (exist) {
						exist.count++
						maxCount = Math.max(maxCount, exist.count)
					} else {
						list.push({ year, count: 1 })
						maxCount = Math.max(maxCount, 1)
					}
				})
			list.sort((a, b) => b.year - a.year)

			const years: { year: number, count: number }[] = []
			if (startYear && lastYear) {
				for(let year = startYear; year <= lastYear; year++) {
					const count = list.find(r => r.year === year)?.count ?? 0
					years.push({ year, count })
				}
			}
			return {
				list: years,
				maxCount
			}
		},
		[filterdArticleList, isFiltered, selectedAuthorId, selectedMesh, selectedPublicationType]
	)
	const articles = useMemo<ArticleResult[]>(
		() => {
			return filterdArticleList.filter(
				article => isFiltered(article, { selectedYear, selectedAuthorId, selectedMesh, selectedPublicationType }) === false
			)
		},
		[filterdArticleList, isFiltered, selectedYear, selectedAuthorId, selectedMesh, selectedPublicationType]
	)
	const authors = useMemo<ArticlesAuthor[]>(
		() => {
      const authorMap: {
        [kojinCode: string]: ArticlesAuthor
      } = {}
			filterdArticleList
				.filter(article => isFiltered(
					article, { selectedYear, selectedMesh, selectedPublicationType }) === false
				)
				.forEach(article => article.authors?.forEach(author => {
					if (!author.kojinCode || !author.sisetuList) return
					const organ = author.sisetuList.find(sisetu => sisetu.sisetuCode === sisetuCode)
					if (!organ) return

          const existAuthor = authorMap[author.kojinCode]
          if (!existAuthor) {
            authorMap[author.kojinCode] = {
							...author,
							buka: organ.buka,
							yakusyoku: organ.yakusyoku
						}
          }
				}))
			const authors = Object.values(authorMap)
			authors.sort((a, b) => b.score - a.score)
			return authors
		},
		[sisetuCode, filterdArticleList, isFiltered, selectedYear, selectedMesh, selectedPublicationType]
	)
	const meshStats = useMemo<MeshStats>(
		() => {
			let maxCount = 0
			const meshMap: {
				[description: string]: { count: number }
			} = {}
			filterdArticleList
				.filter(article => isFiltered(
					article, { selectedYear, selectedAuthorId, selectedPublicationType }) === false
				)
				.forEach(article => article.meshs?.forEach(mesh => {
					if (!mesh.isMajor) return
					const description = mesh.descriptionJpn ?? mesh.description
          const exist = meshMap[mesh.description]
          if (exist) {
            exist.count++
						maxCount = Math.max(maxCount, exist.count)
          } else {
            meshMap[description] = { count: 1 }
						maxCount = Math.max(maxCount, 1)
          }
				}))
			const meshs = Object.entries(meshMap).map(([description, info]) => ({ description, count: info.count }))
			meshs.sort((a, b) => b.count - a.count)
			return {
				maxCount,
				list: meshs
			}
		},
		[filterdArticleList, isFiltered, selectedYear, selectedAuthorId, selectedPublicationType]
	)
	const publicationTypeStats = useMemo<PublicationTypeStats>(
		() => {
			let maxCount = 0
			const publicationTypeMap: {
				[type: string]: { count: number }
			} = {}
			filterdArticleList
				.filter(article => isFiltered(
					article, { selectedYear, selectedAuthorId, selectedPublicationType }) === false
				)
				.forEach(article => article.publicationType?.forEach(publicationType => {
					const type = publicationType.name
          const exist = publicationTypeMap[type]
          if (exist) {
            exist.count++
						maxCount = Math.max(maxCount, exist.count)
          } else {
            publicationTypeMap[type] = { count: 1 }
						maxCount = Math.max(maxCount, 1)
          }
				}))
			const publicationTypeMaps = Object.entries(publicationTypeMap)
				.map(([type, info]) => ({ type, count: info.count }))
			publicationTypeMaps.sort((a, b) => b.count - a.count)
			return {
				maxCount,
				list: publicationTypeMaps
			}
		},
		[filterdArticleList, isFiltered, selectedYear, selectedAuthorId, selectedPublicationType]
	)
	return {
		searchWord, setSearchWord,
		selectedYear, setSelectedYear,
		selectedAuthorId, setSelectedAuthorId,
		selectedMesh, setSelectedMesh,
		selectedPublicationType, setSelectedPublicationType,
		loading: listState.loading,
		error: listState.error,
		articles, yearsStats, authors, meshStats, publicationTypeStats
	}
}